import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import { formatClinicDate, clinicNow } from '../../utils/clinicTime';
import { PAYMENT_MODE_LABEL } from '../../constants/payments';
import { rupeesInUrduWords } from '../../utils/urduNumbers';

// Bits every Expenses page shares: formatting, the date presets, the department pills and the
// category tree loaded once for the module.

export const formatCurrency = (amt) => `Rs ${Math.round(Number(amt || 0)).toLocaleString('en-US')}`;

/** A signed figure for the ledger: withdrawals add to the cash box, expenses take from it. */
export const formatSignedAmount = (entry) =>
  `${entry.entryType === 'WITHDRAWAL' ? '+' : '-'} ${formatCurrency(entry.amount)}`;

export const formatDate = (iso) => {
  if (!iso) return '-';
  return formatClinicDate(iso, { day: '2-digit', month: 'short', year: 'numeric' });
};

export const toISO = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Today in the clinic's zone, as YYYY-MM-DD. */
export const todayISO = () => toISO(clinicNow());

export const getDatePreset = (preset) => {
  // Clinic-zone "today" so expense date ranges match the backend's clinic-day filtering.
  const today = clinicNow();
  today.setHours(0, 0, 0, 0);
  const from = new Date(today);
  const to = new Date(today);
  switch (preset) {
    case 'today':
      break;
    case 'yesterday':
      from.setDate(from.getDate() - 1);
      to.setDate(to.getDate() - 1);
      break;
    case 'last7':
      from.setDate(from.getDate() - 6);
      break;
    case 'last30':
      from.setDate(from.getDate() - 29);
      break;
    case 'thisMonth':
      from.setDate(1);
      break;
    case 'lastMonth': {
      from.setMonth(from.getMonth() - 1); from.setDate(1);
      to.setDate(0); // last day of previous month
      break;
    }
    case 'thisYear':
      from.setMonth(0); from.setDate(1);
      break;
    case 'all':
      return { from: '', to: '' };
    default:
      break;
  }
  return { from: toISO(from), to: toISO(to) };
};

export const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'thisYear', label: 'This Year' },
  { key: 'all', label: 'All Time' },
];

export const ENTRY_TYPE_CONFIG = {
  EXPENSE: { label: 'Expense', color: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  WITHDRAWAL: { label: 'Withdrawal', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
};

// Colours for the badge only. Which methods can be CHOSEN comes from the shared list; this map
// still carries the retired ones so an old entry's badge renders instead of falling back to blank.
export const PAYMENT_MODE_CONFIG = {
  BANK_TRANSFER: { label: 'Bank Transfer', color: 'bg-blue-100 text-blue-800' },
  CARD: { label: 'Card', color: 'bg-amber-100 text-amber-800' },
  CASH: { label: 'Cash', color: 'bg-emerald-100 text-emerald-800' },
  CHEQUE: { label: 'Cheque', color: 'bg-purple-100 text-purple-800' },
  MOBILE_WALLET: { label: 'Mobile Wallet', color: 'bg-rose-100 text-rose-800' },
  JAZZCASH: { label: 'JazzCash', color: 'bg-rose-100 text-rose-800' },
  EASYPAISA: { label: 'EasyPaisa', color: 'bg-lime-100 text-lime-800' },
  ONLINE: { label: 'Online', color: 'bg-cyan-100 text-cyan-800' },
  OTHER: { label: 'Other', color: 'bg-gray-100 text-gray-700' },
};

export const paymentLabel = (mode) =>
  (PAYMENT_MODE_CONFIG[mode] || {}).label || PAYMENT_MODE_LABEL[mode] || mode || '-';

/** The reference an entry carries: a cheque number or a transfer reference. */
export const referenceOf = (entry) => entry.chequeNumber || entry.txnNumber || '';

/** The message of a failed API call, in words a bookkeeper can act on. */
export const errorText = (err, fallback = 'Something went wrong') =>
  (err && (err.message || (err.body && (err.body.error || err.body.message)))) || fallback;

// ---------------------------------------------------------------------------------------------
// Department: remembered across pages and visits. 'ALL' or a root category id.
// ---------------------------------------------------------------------------------------------

const DEPARTMENT_KEY = 'clinic_expense_department';

export const readDepartment = () => {
  try { return localStorage.getItem(DEPARTMENT_KEY) || 'ALL'; } catch (e) { return 'ALL'; }
};

export const writeDepartment = (value) => {
  try { localStorage.setItem(DEPARTMENT_KEY, value || 'ALL'); } catch (e) { /* ignore */ }
};

/**
 * The category tree for the module, loaded once and shared. `roots` are the departments,
 * `flat` every category with its parent, `childrenOf(rootId)` a department's categories.
 */
export const useExpenseCategories = () => {
  const showToast = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await expenseAPI.getCategories(true);
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast('error', 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { reload(); }, [reload]);

  const roots = useMemo(() => categories.filter(c => c.active !== false), [categories]);
  const flat = useMemo(() => {
    const out = [];
    categories.forEach(cat => {
      out.push({ id: cat.id, name: cat.name, active: cat.active, parentId: null, parentName: null });
      (cat.children || []).forEach(child => {
        out.push({ id: child.id, name: child.name, active: child.active, parentId: cat.id, parentName: cat.name });
      });
    });
    return out;
  }, [categories]);
  const byId = useMemo(() => Object.fromEntries(flat.map(c => [c.id, c])), [flat]);
  const childrenOf = useCallback(
    (rootId) => (categories.find(c => c.id === rootId)?.children || []).filter(c => c.active !== false),
    [categories],
  );
  const pathOf = useCallback((id) => {
    const c = byId[id];
    if (!c) return '-';
    return c.parentName ? `${c.parentName} › ${c.name}` : c.name;
  }, [byId]);
  /** The department an id belongs to: itself when it is a root. */
  const rootOf = useCallback((id) => {
    const c = byId[id];
    if (!c) return null;
    return c.parentId || c.id;
  }, [byId]);

  return { categories, roots, flat, byId, childrenOf, pathOf, rootOf, loading, reload };
};

/**
 * The department pills: All departments, then each department by name. `value` is 'ALL' or a
 * root id. With `required` there is no All pill, for the forms, where an entry must be filed.
 */
export const DepartmentPills = ({ roots, value, onChange, required = false, size = 'md', label }) => {
  const pad = size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm';
  const pills = required ? roots : [{ id: 'ALL', name: 'All departments' }, ...roots];
  return (
    <div role="group" aria-label={label || 'Department'} className="flex items-center gap-2 flex-wrap">
      {!required && <Building2 className="h-5 w-5 text-gray-400" aria-hidden="true" />}
      {pills.map(r => {
        const active = value === r.id;
        return (
          <button
            key={r.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(r.id)}
            className={`${pad} rounded-lg font-medium transition-colors border ${
              active
                ? 'bg-navy-800 text-white border-navy-800'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            {r.name}
          </button>
        );
      })}
      {required && roots.length === 0 && (
        <span className="text-sm text-gray-400">Add a department under Categories first.</span>
      )}
    </div>
  );
};

/** The row of date preset pills used by the Dashboard and the Entries page. */
export const DatePresetPills = ({ value, onChange }) => (
  <div className="flex items-center gap-2 flex-wrap">
    {DATE_PRESETS.map(p => (
      <button
        key={p.key}
        type="button"
        onClick={() => onChange(p.key)}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          value === p.key ? 'bg-navy-100 text-navy-800' : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

export const TypeBadge = ({ type }) => {
  const conf = ENTRY_TYPE_CONFIG[type] || ENTRY_TYPE_CONFIG.EXPENSE;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${conf.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${conf.dot}`} />
      {conf.label}
    </span>
  );
};

export const PaymentBadge = ({ mode }) => {
  const conf = PAYMENT_MODE_CONFIG[mode] || {};
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${conf.color || 'bg-gray-100 text-gray-700'}`}>
      {conf.label || mode}
    </span>
  );
};

/** The loading row used inside cards. */
/**
 * A figure in Urdu words under the figure itself, for the bookkeeper who reads a sum more surely
 * in words. Right-aligned: it is a line of RTL text, and Nastaliq needs the extra line height.
 */
export const UrduAmount = ({ value, className = 'text-gray-500' }) => (
  <p className={`urdu-text text-sm mt-1 ${className}`} dir="rtl" lang="ur" style={{ lineHeight: 2.2 }}>
    {rupeesInUrduWords(value)}
  </p>
);

export const LoadingRow = ({ text = 'Loading...' }) => (
  <div className="flex items-center justify-center py-16 text-gray-500">
    <span className="h-5 w-5 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin mr-3" />
    {text}
  </div>
);

export const inputClass =
  'w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none';
export const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';
